import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const NavWrapper = styled(Box)(({ scrolled, isLightSection }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  padding: scrolled ? '12px 60px' : '24px 60px',
  background: scrolled
    ? isLightSection
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(15, 15, 15, 0.95)'
    : 'transparent',
  backdropFilter: scrolled ? 'blur(20px)' : 'none',
  borderBottom: scrolled
    ? isLightSection
      ? '1px solid rgba(0, 0, 0, 0.08)'
      : '1px solid rgba(54, 214, 208, 0.1)'
    : 'none',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${slideDown} 0.6s ease-out`,
  '@media (max-width: 768px)': {
    padding: scrolled ? '12px 20px' : '20px 20px'
  }
}));

const NavContainer = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const NavLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center'
});

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer'
});

const LogoMark = styled(Box)({
  width: '40px',
  height: '40px',
  background: 'linear-gradient(135deg, #36d6d0 0%, #b8fff5 100%)',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: '20px',
  fontWeight: 700,
  color: '#1b1f3a',
  boxShadow: '0 4px 15px rgba(54, 214, 208, 0.3)'
});

const LogoText = styled(Typography)(({ isLightSection }) => ({
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: '22px',
  fontWeight: 600,
  color: isLightSection ? '#1a1a1a' : '#ffffff',
  letterSpacing: '-0.5px',
  transition: 'color 0.4s ease',
  '@media (max-width: 480px)': {
    display: 'none'
  }
}));

const NavCenter = styled(Box)(({ isLightSection }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px',
  background: isLightSection ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.05)',
  borderRadius: '50px',
  border: isLightSection ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.4s ease',
  '@media (max-width: 900px)': {
    display: 'none'
  }
}));

const NavLink = styled(Box)(({ active, isLightSection }) => ({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: active
    ? '#1b1f3a'
    : isLightSection
      ? 'rgba(26, 26, 26, 0.7)'
      : 'rgba(255, 255, 255, 0.7)',
  padding: '10px 20px',
  borderRadius: '50px',
  cursor: 'pointer',
  background: active ? 'linear-gradient(135deg, #36d6d0 0%, #b8fff5 100%)' : 'transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: active ? '#1b1f3a' : isLightSection ? '#1a1a1a' : '#ffffff',
    background: active
      ? 'linear-gradient(135deg, #36d6d0 0%, #b8fff5 100%)'
      : isLightSection
        ? 'rgba(0, 0, 0, 0.06)'
        : 'rgba(255, 255, 255, 0.1)'
  }
}));

const NavRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px'
});

const CTAButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 28px',
  background: 'linear-gradient(135deg, #36d6d0 0%, #b8fff5 100%)',
  borderRadius: '50px',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 4px 20px rgba(54, 214, 208, 0.3)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 30px rgba(54, 214, 208, 0.4)'
  },
  '@media (max-width: 900px)': {
    display: 'none'
  }
});

const CTAText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#1b1f3a'
});

const MobileMenuBtn = styled(IconButton)(({ isLightSection }) => ({
  display: 'none',
  width: '44px',
  height: '44px',
  color: isLightSection ? '#1a1a1a' : '#ffffff',
  background: isLightSection ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
  border: isLightSection ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: isLightSection ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'
  },
  '@media (max-width: 900px)': {
    display: 'flex'
  }
}));

const MobileDrawer = styled(Box)(({ open }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  width: '100%',
  height: '100vh',
  background: 'linear-gradient(135deg, #0b1125 0%, #1b1f3a 100%)',
  transform: open ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 1002,
  padding: '100px 32px 40px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto'
}));

const Overlay = styled(Box)(({ open }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  transition: 'all 0.4s ease',
  zIndex: 1001
}));

const MobileNavLink = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '28px',
  fontWeight: 300,
  color: 'rgba(255, 255, 255, 0.7)',
  padding: '20px 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#36d6d0',
    paddingLeft: '16px'
  }
});

const CloseBtn = styled(IconButton)({
  position: 'absolute',
  top: '24px',
  right: '24px',
  width: '48px',
  height: '48px',
  color: '#ffffff',
  background: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '12px',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)'
  }
});

const MobileCTA = styled(Box)({
  marginTop: 'auto',
  padding: '18px 32px',
  background: 'linear-gradient(135deg, #36d6d0 0%, #b8fff5 100%)',
  borderRadius: '50px',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '16px',
  fontWeight: 600,
  textAlign: 'center',
  cursor: 'pointer',
  color: '#1b1f3a',
  boxShadow: '0 4px 30px rgba(54, 214, 208, 0.3)'
});

const MobileFooter = styled(Box)({
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
});

const MobileFooterText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  color: 'rgba(255, 255, 255, 0.4)',
  textAlign: 'center'
});

const navItems = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' }
];

// Light section IDs - sections with light/white backgrounds
const LIGHT_SECTIONS = ['about', 'hours', 'contact', 'updates', 'tags', 'related-services'];

const NavigationHeader = ({ businessInfo, contactInfo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isLightSection, setIsLightSection] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Detect active section and if it's a light section
      const sections = navItems.map(item => item.href.replace('#', ''));
      const allSections = [...sections, 'hours', 'tags', 'related-services'];

      let currentSection = 'home';
      for (const section of allSections.reverse()) {
        const el = document.getElementById(section);
        if (el && el.getBoundingClientRect().top <= 100) {
          currentSection = section;
          break;
        }
      }

      setActiveSection(currentSection);
      setIsLightSection(LIGHT_SECTIONS.includes(currentSection));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const businessName = businessInfo?.name || 'Business';
  const firstLetter = businessName.charAt(0).toUpperCase();

  return (
    <>
      <NavWrapper scrolled={scrolled} isLightSection={isLightSection}>
        <NavContainer>
          <NavLeft>
            <LogoContainer onClick={() => handleNavClick('#home')}>
              <LogoMark>{firstLetter}</LogoMark>
              <LogoText isLightSection={isLightSection}>{businessName}</LogoText>
            </LogoContainer>
          </NavLeft>

          <NavCenter isLightSection={isLightSection}>
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                active={activeSection === item.href.replace('#', '')}
                isLightSection={isLightSection}
                onClick={() => handleNavClick(item.href)}
              >
                {item.label}
              </NavLink>
            ))}
          </NavCenter>

          <NavRight>
            <CTAButton onClick={() => handleNavClick('#contact')}>
              <CTAText>Get Started</CTAText>
            </CTAButton>
            <MobileMenuBtn isLightSection={isLightSection} onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </MobileMenuBtn>
          </NavRight>
        </NavContainer>
      </NavWrapper>

      <Overlay open={mobileOpen} onClick={() => setMobileOpen(false)} />

      <MobileDrawer open={mobileOpen}>
        <CloseBtn onClick={() => setMobileOpen(false)}>
          <CloseIcon />
        </CloseBtn>

        {navItems.map((item) => (
          <MobileNavLink key={item.label} onClick={() => handleNavClick(item.href)}>
            {item.label}
          </MobileNavLink>
        ))}

        <MobileCTA onClick={() => handleNavClick('#contact')}>
          Get Started
        </MobileCTA>

        <MobileFooter>
          <MobileFooterText>
            {businessName} - Premium Services
          </MobileFooterText>
        </MobileFooter>
      </MobileDrawer>
    </>
  );
};

export default NavigationHeader;



